import { GDT } from "@/types";
import { get, pick, zipObject } from "lodash-es";
import { Point, Geometry } from "geojson";
import { AddressIdType, ColumnDefinition } from "@gamma/types.data";
import { GammaAccuracy } from "@/types/location/location-accuracy";

export class DatasetRecord {
  constructor(
    public readonly data: unknown[],
    public readonly columnDefinitions: GDT.ColumnDefinition[],
    public readonly props: Record<string, any> = {}
  ) {}

  get uniqueIdentifier(): string {
    const columnIndexes = this.columnDefinitions
      .map((cd, i) => (cd.context === "UniqueId" ? i : -1))
      .filter((n) => n >= 0);
    return this.data
      .filter((value: any, i) => columnIndexes.includes(i))
      .join("/");
  }

  get query(): Record<string, unknown> {
    const columnNames = this.columnDefinitions
      .map((cd) => (cd.context === "UniqueId" ? cd.columnName : ""))
      .filter((n) => n);
    return pick(this.value, columnNames);
  }

  private get queryFilter(): GDT.DataFilter[] {
    return Object.entries(this.query).map(([fieldName, value]) => ({
      fieldName,
      value,
      filterOperator: 0,
      conditionOperator: 0,
    }));
  }

  get recordType(): "Policy" | "Claim" {
    return get(this.value, "recordType");
  }

  private get propIndexes(): number[] {
    return this.columnDefinitions
      .map((c, i) => (!c.visible ? null : i))
      .filter((i) => i !== null) as number[];
  }

  private get addressIndexes(): number[] {
    return this.columnDefinitions
      .map((c, i) => (c.context === "AddressLine" ? i : null))
      .filter((i) => i !== null) as number[];
  }

  get value(): Record<string, any> {
    return {
      ...zipObject(
        this.propIndexes.map((i) => this.columnDefinitions[i].columnName),
        this.propIndexes.map((i) => this.data[i])
      ),
      addressLine: this.addressLine,
      ...this.props,
    };
  }

  get visibleProps(): Record<string, unknown> {
    return pick(
      this.value,
      this.columnDefinitions.filter((c) => c.visible).map((c) => c.columnName)
    );
  }

  get geometry(): Geometry | null {
    if (this.value.geometry) {
      return this.value.geometry;
    }
    const index = this.columnDefinitions.findIndex(
      (c) => c.type === "Geometry"
    );
    if (index > -1) {
      return this.data[index] as Geometry;
    }
    return null;
  }

  private get addressLine(): string {
    return this.addressLineArray.join(", ");
  }

  get requestPartial() {
    const index = this.columnDefinitions.findIndex(
      (c) => c.context === "AddressId"
    );
    const addressId = this.data[index];
    if (addressId) {
      switch (
        (this.columnDefinitions[index].contextInfo?.addressIdType ||
          AddressIdType.Undefined) as AddressIdType
      ) {
        case AddressIdType.EcadId:
          return { ecadId: addressId };
        case AddressIdType.UPRN:
          return { uprn: addressId };
        default:
          return { addressId };
      }
    }
    return {};
  }

  get columnDefinitionsMap(): Map<string, GDT.ColumnDefinition> {
    return this.columnDefinitions.reduce(
      (result, item) => result.set(item.columnName, item),
      new Map<string, GDT.ColumnDefinition>()
    );
  }

  get addressLineArray(): string[] {
    return this.addressIndexes
      .map((i) => this.data[i])
      .filter(Boolean) as string[];
  }

  get hasMultipleSumInsured(): boolean {
    return this.sumInsuredColumns.length > 1;
  }

  get sumInsuredColumns(): ColumnDefinition[] {
    return this.columnDefinitions.filter((c) => c.context === "SumInsured");
  }

  get totalSumInsured(): number | undefined {
    return this.hasMultipleSumInsured
      ? this.sumInsuredColumns.reduce(
          (x, y) => x + (get(this.value, y.columnName) || 0),
          0
        )
      : this.sumInsured;
  }

  private getContextColumn<T>(context: string) {
    const index = this.columnDefinitions.findIndex(
      (c) => c.context === context
    );
    return index > -1 ? (this.data[index] as T | undefined) : undefined;
  }

  private get sumInsured() {
    return this.getContextColumn<number>("SumInsured");
  }

  get precisionAccuracy(): GammaAccuracy | undefined {
    return this.getContextColumn<GammaAccuracy>("LocationAccuracyV1");
  }

  get lcId(): string {
    return this.props.lcId || "";
  }

  get toJson(): GDT.DatasetRecordIdentifier {
    return {
      geometry: this.geometry || undefined,
      lcId: this.lcId,
      query: this.query,
    };
  }

  updateGeometryQuery(value: Point) {
    const columnName =
      this.columnDefinitions.find((cd) => cd.type === "Geometry")?.columnName ||
      "";
    return columnName
      ? {
          columnUpdates: [
            {
              column: { columnName },
              value,
            },
          ],
          filter: this.queryFilter,
        }
      : null;
  }

  updateGroupQuery(value: string): GDT.UpdateRecordRequest | null {
    const columnName = "GROUP_NUMBER"; // ToDo: use _columnDefinitions.columns.context
    return columnName
      ? {
          columnUpdates: [
            {
              column: { columnName },
              value,
            },
          ],
          filter: this.queryFilter,
        }
      : null;
  }

  updateHitcodeNumQuery(code: number): GDT.UpdateRecordRequest | null {
    const columnNum = "HIT_CODE";
    return code
      ? {
          columnUpdates: [
            {
              column: { columnName: columnNum },
              value: code,
            },
          ],
          filter: this.queryFilter,
        }
      : null;
  }
}
